import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Upload, Loader2, AlertCircle } from "lucide-react";

export default function KycPage() {
  const { user, refreshUser } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const returnTo = new URLSearchParams(location.search).get("returnTo") || "/owner/cars/new";
  const [kycInfo, setKycInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docType, setDocType] = useState("national_id");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/kyc/me")
      .then(({ data }) => setKycInfo(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleUpload = async () => {
    if (!file) { toast.error("Please select a document"); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("document_type", docType);
      formData.append("file", file);
      const { data } = await api.post("/kyc/verify-document", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setKycInfo(prev => ({ ...prev, kyc_status: data.status }));
      if (data.status === "verified") {
        toast.success("Identity verified! Redirecting to your listing form…");
      } else {
        toast.success("Uploaded — verification is running in the background. You can continue.");
      }
      if (refreshUser) await refreshUser();
      setTimeout(() => nav(returnTo), 1500);
    } catch(e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="animate-spin text-primary" size={32} />
    </div>
  );

  const isVerified = kycInfo?.kyc_status === "verified";

  return (
    <div className="max-w-xl space-y-6 py-2">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Identity Verification</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Verify your identity once to book cars and list your own vehicle.
        </p>
      </div>

      {/* Status */}
      <Card className="p-6 border-border">
        <div className="flex items-center gap-4">
          <div className={"w-12 h-12 rounded-full flex items-center justify-center shrink-0 " + (isVerified ? "bg-green-100" : "bg-amber-100")}>
            {isVerified
              ? <CheckCircle2 size={24} className="text-green-600" />
              : <AlertCircle size={24} className="text-amber-600" />}
          </div>
          <div>
            <div className="font-display font-semibold">
              {isVerified ? "Identity Verified ✓" : "Not yet verified"}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              {isVerified
                ? "Your identity is verified. You can book and list cars freely."
                : "Upload one document to verify your identity. You only do this once."}
            </div>
          </div>
        </div>
      </Card>

      {!isVerified && (
        <Card className="p-6 border-border space-y-4">
          <div className="text-sm font-semibold">Upload an identity document</div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-2">Document type</label>
            <div className="flex gap-2 flex-wrap">
              {[["national_id","National ID"],["passport","Passport"],["driving_licence","Driving Licence"]].map(([val,label]) => (
                <button key={val} type="button" onClick={() => setDocType(val)}
                  className={"px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors " + (docType === val ? "bg-primary text-white border-primary" : "border-border text-muted-foreground hover:bg-muted")}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold block mb-2">Upload document</label>
            <div
              className={"border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors " + (file ? "border-primary bg-primary/5" : "border-border hover:border-primary/50")}
              onClick={() => document.getElementById("kyc-dash-file").click()}>
              <Upload size={28} className="mx-auto text-muted-foreground mb-2" />
              {file
                ? <p className="text-sm font-medium text-primary">{file.name}</p>
                : <>
                    <p className="text-sm text-muted-foreground">Click to upload photo or PDF</p>
                    <p className="text-xs text-muted-foreground mt-1">Supports: JPG, PNG, PDF</p>
                  </>}
              <input id="kyc-dash-file" type="file" className="hidden"
                onChange={e => setFile(e.target.files[0])} />
            </div>
          </div>

          <div className="text-xs text-muted-foreground flex items-start gap-2 bg-muted p-3 rounded-lg">
            <ShieldCheck size={14} className="text-primary mt-0.5 shrink-0" />
            <span>Your document is verified automatically. We check your name and document validity.</span>
          </div>

          <Button onClick={handleUpload} disabled={uploading || !file} className="w-full h-11 bg-primary hover:bg-primary/90">
            {uploading
              ? <><Loader2 size={16} className="mr-2 animate-spin" /> Verifying…</>
              : <><ShieldCheck size={16} className="mr-2" /> Verify my identity</>}
          </Button>
        </Card>
      )}
    </div>
  );
}
